import SoundManager from "../../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import Anchor from "../../../../../lib/html5-software-library/src/ranj/layout/Anchor";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import AssetLibrary from "../../../core/assetloading/AssetLibrary";
import Config from "../../../core/config/Config";
import SoundType from "../../../core/sound/util/SoundType";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import TextTypeWriterAnimation from "../../../core/ui/animation/typewriters/TextTypeWriterAnimation";
import ClickEaterFactory from "../../../core/ui/ClickEaterFactory";
import ScenarioLibrary from "../../../ngt/scenario/ScenarioLibrary";
import BackendService from "../../backend/controller/BackendService";
import Conversation from "../../conversation/model/Conversation";
import DebugModel from "../../debug/model/DebugModel";
import PlayerModel from "../../player/model/PlayerModel";
import DisplayObjectUtil from "../../util/DisplayObjectUtil";
import LlmMessagePrefixer from "../../util/LlmMessagePrefixer";
import PromiseUtil from "../../util/PromiseUtil";
import ConversationFeedbackEvent from "../event/ConversationFeedbackEvent";

export default class ConversationFeedbackScreenMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LayoutManager} layoutManager 
     * @param {ClickEaterFactory} clickEaterFactory 
     * @param {RevisableLanguagePack} languagePack 
     * @param {ScenarioLibrary} scenarioLibrary 
     * @param {AssetLibrary} assetLibrary 
     * @param {BackendService} backendService 
     * @param {PlayerModel} playerModel
     * @param {Config} config
     * @param {SoundManager} soundManager
     */
    constructor(target, dispatcher, layoutManager, clickEaterFactory, languagePack, scenarioLibrary, assetLibrary, backendService, playerModel, config, soundManager) {
        super(target, dispatcher, layoutManager, clickEaterFactory);

        this._languagePack = languagePack;
        this._scenarioLibrary = scenarioLibrary;
        this._assetLibrary = assetLibrary;
        this._backendService = backendService;
        this._playerModel = playerModel;
        this._config = config;
        this._soundManager = soundManager

        /** @type {Conversation} */
        this._conversation = this.getCustomData().conversation;

        this._currentPage = 0;
        this._numberOfPages = 1;
        this._typeWriteAnimations = [];
    }

    async initialize() {
        super.initialize();

        this.preventClickThrough();

        this._initializeBackgroundImage();

        this._setHeader();
        this._target.contentTextHeader.text = this._languagePack.getText(`coachFeedbackScreen.title`);
        this._target.panel.contentHeaderYou.text = this._languagePack.getText(`coachFeedbackScreen.youSaid`);
        this._target.panel.contentFeedbackHeader.text = this._languagePack.getText(`coachFeedbackScreen.feedback`);

        this._nextButton = this.createBracketButton(this._target.panel.continueButton, this._handleNextButton.bind(this), this._languagePack.getText(`coachFeedbackScreen.continueButtonLabel`), SoundType.BRACKET_BUTTON_CLICK);
        this._backButton = this.createBracketButton(this._target.panel.backButton, this._handleBackButton.bind(this), this._languagePack.getText(`coachFeedbackScreen.backButtonLabel`), SoundType.BRACKET_BUTTON_CLICK);

        const maxFeedbackItems = this._config.getValue(`feedback.maxNumberOfItems`);
        const numberOfFeedbackItems = Math.min(this._conversation.feedback.length, maxFeedbackItems);
        this._feedback = this._conversation.feedback.slice(0, numberOfFeedbackItems); // Limit the number of feedback items, as it is hard to instruct a LLM to only return N feedback items.
        this._numberOfPages = this._feedback.length - 1;
        this._updateFeedback();

        this._animateCoach(650); // With delay to account for the transition.
    }

    _initializeBackgroundImage() {
        this._target.bgImageContainer.gotoAndStop(0); // Ensures that the children are populated.
        this._target.bgImageContainer.removeAllChildren();

        const imageId = this._scenarioLibrary.getCurrentScenario().feedbackBackgroundImageId;
        const image = this._assetLibrary.getAsset(imageId);
        if (!image) {
            console.warn(`Image with id ${imageId} does not exist. Ignoring request.`);
            return;
        }

        const bitmap = new createjs.Bitmap(image);
        DisplayObjectUtil.anchorDisplayObject(bitmap, Anchor.C);
        this._target.bgImageContainer.addChild(bitmap);
    }

    _finish() {
        this.disableInteraction();

        this._soundManager.stop(true, 1000);

        const conversationFeedbackEvent = new ConversationFeedbackEvent(ConversationFeedbackEvent.FINISH, this._conversation);
        this.dispatch(conversationFeedbackEvent.type, { data: conversationFeedbackEvent });
    }

    async _updateFeedback() {
        const currentText = this._feedback[this._currentPage];
        const strippedQuote = currentText.quote.replace(LlmMessagePrefixer.getPlayerMessagePrefix(this._playerModel.getTrimmedPlayerName()), ''); // Sometimes the LLM won't remove the `Bob (speler): ` prefix. Here we force it to.
        const quoteText = `> "${strippedQuote}"`;
        const feedbackText = `* ${currentText.feedback}`;

        this._target.panel.contentTextYou.text = '';
        this._target.panel.contentFeedback.text = '';

        this._forceFinishCurrentTypeWriters();
        const quoteTextWriteDuration = this._getTypeWriterDuration(quoteText);

        const typewriteSoundId = this._soundManager.playSound(SoundType.TYPEWRITER_COACH, null, null, 0, 0, -1);
        this._typeWriteText(this._target.panel.contentTextYou, quoteText);
        await this._typeWriteText(this._target.panel.contentFeedback, feedbackText, quoteTextWriteDuration);
        this._soundManager.stopSound(typewriteSoundId, 50);

        // Disable back button on the first page
        if (this._currentPage == 0) {
            this._backButton.setEnabled(false);
        } else {
            this._backButton.setEnabled(true);
        }

        // Disable back button on the first page
        if (this._currentPage >= this._numberOfPages) {
            this._nextButton.setLabel(this._languagePack.getText(`coachFeedbackScreen.finishButtonLabel`));
        } else {
            this._nextButton.setLabel(this._languagePack.getText(`coachFeedbackScreen.continueButtonLabel`));
        }
    }

    _handleNextButton() {
        if (this._currentPage >= this._numberOfPages) {
            this._finish();
            return;
        }

        this._currentPage++;
        this._updateFeedback();
    }

    _handleBackButton() {
        if (this._currentPage <= 0) {
            return;
        }

        this._currentPage--;
        this._updateFeedback();
    }

    _setHeader() {
        const rawText = this._languagePack.getText(`coachFeedbackScreen.headerTexts.${this._conversation.id}`);
        const playerName = this._playerModel.getTrimmedPlayerName();
        const processedText = rawText.replace(/{{playerName}}/g, playerName);
        this._target.panel.contentTextHeader.text = processedText;
    }

    async _animateCoach(delay = 0) {
        await PromiseUtil.delay(delay);
        this._soundManager.playSound(SoundType.COACH_SHOW);
        this._target.robbyReflection.play();
    }

    async _typeWriteText(textField, targetText = "", delay = 0) {
        await new Promise(resolve => {
            textField.text = "";

            const duration = this._getTypeWriterDuration(targetText);
            const typeWriterAnimation = TextTypeWriterAnimation.animate(textField, targetText, duration, delay, resolve);
            this._typeWriteAnimations.push(typeWriterAnimation);
        });
    }

    _getTypeWriterDuration(targetText) {
        const timePerCharacter = DebugModel.fasterAnimations ? 0.0001 : 0.015;
        return targetText.length * timePerCharacter;
    }

    _forceFinishCurrentTypeWriters() {
        this._typeWriteAnimations.forEach(animation => {
            animation.forceFinish();
        });
        this._typeWriteAnimations = [];
    }
}